import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import './App.css';
import NavBar from './layouts/NavBar';
import WhatsAppButton from './layouts/WhatsAppButton';
import ProcessSection from './layouts/ProcessSection';
import Footer from './layouts/Footer';
import Contact from './layouts/Contact';
import About from './layouts/About';
import { PrivacyPolicyPage } from './layouts/PrivacyPolicy';
import { TermsOfUsePage }    from './layouts/TermsOfUse';
import PompomFaq from './modules/pompom/pages/PompomFaq';
import FauxFurPomPomsPage from './modules/pompom/pages/FauxFurPomPomsPage';
import FauxFurPomPomsFaqPage from './modules/pompom/pages/FauxFurPomPomsFaqPage';
import PomPomsPage from './modules/pompom/pages/PomPomsPage';
import DesignerTasselsPage from './modules/pompom/pages/DesignerTasselsPage';
import BeadedPatchesPage from './modules/beadedpatch/pages/BeadedPatchesPage';
import FringesPage from './modules/pompom/pages/FringesPage';
import ProductsPage from './modules/products/pages/ProductsPage';
import ProductDetailPage from './modules/products/pages/ProductDetailPage';
import CartPage from './modules/users/pages/CartPage';
import { WishlistPage } from './modules/users/pages/WishlistPage';
import CheckoutPage from './modules/users/pages/CheckoutPage';
import OrderConfirmationPage from './modules/users/pages/OrderConfirmationPage';
import SearchPage from './modules/products/pages/SearchPage';
import CategoriesPage from './modules/categoryShowcase/pages/CategoriesPage';
import CategoryDetailPage from './modules/categoryShowcase/pages/CategoryDetailPage';
import TestimonialsPage from './modules/testimonials/pages/TestimonialsPage';

import { ProtectedRoute } from './shared/components/ProtectedRoute';
import { UnauthorizedPage } from './shared/pages/UnauthorizedPage';
import { NotFoundPage } from './shared/pages/NotFoundPage';

// Auth pages
import { LoginPage }           from './modules/users/pages/LoginPage';
import { RegisterPage }        from './modules/users/pages/RegisterPage';
import { ForgotPasswordPage }  from './modules/users/pages/ForgotPasswordPage';
import { ResetPasswordPage }   from './modules/users/pages/ResetPasswordPage';
import { VerifyEmailPage }     from './modules/users/pages/VerifyEmailPage';
import { SellerRegisterPage }  from './modules/seller/pages/SellerRegisterPage';

// Lazy-loaded role module routes (code-splitting per role)
const UserRoutes       = lazy(() => import('./modules/users/routes/UserRoutes').then(m => ({ default: m.UserRoutes })));
const SellerRoutes     = lazy(() => import('./modules/seller/routes/SellerRoutes').then(m => ({ default: m.SellerRoutes })));
const AdminRoutes      = lazy(() => import('./modules/admin/routes/AdminRoutes').then(m => ({ default: m.AdminRoutes })));
const AgentRoutes      = lazy(() => import('./modules/agent/routes/AgentRoutes').then(m => ({ default: m.AgentRoutes })));
const SuperAdminRoutes = lazy(() => import('./modules/supperadmin/routes/SuperAdminRoutes').then(m => ({ default: m.SuperAdminRoutes })));
const QuoteRoutes      = lazy(() => import('./modules/requestaquote/routes/QuoteRoutes'));

const Loading = () => <div style={{ textAlign: 'center', padding: 60 }}>Loading…</div>;

// Routes that show the public NavBar + Footer
const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
        <NavBar />
        {children}
        <Footer />
    </>
);

const App: React.FC = () => (
    <BrowserRouter>
        <WhatsAppButton />
        <Routes>
            {/* ── Public marketing routes (with NavBar + Footer) ─── */}
            <Route path="/" element={<PublicLayout><ProcessSection /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/about"   element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/pom-pom-faq"         element={<PublicLayout><PompomFaq /></PublicLayout>} />
            <Route path="/faux-fur-pom-poms"   element={<PublicLayout><FauxFurPomPomsPage /></PublicLayout>} />
            <Route path="/faux-fur-pom-poms-faq" element={<PublicLayout><FauxFurPomPomsFaqPage /></PublicLayout>} />
            <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
            <Route path="/terms-of-use"   element={<PublicLayout><TermsOfUsePage /></PublicLayout>} />
            <Route path="/products"           element={<PublicLayout><ProductsPage /></PublicLayout>} />
            <Route path="/products/pom-poms"         element={<PublicLayout><PomPomsPage /></PublicLayout>} />
            <Route path="/products/faux-fur-pom-poms" element={<Navigate to="/faux-fur-pom-poms" replace />} />
            <Route path="/products/tassels"        element={<PublicLayout><DesignerTasselsPage /></PublicLayout>} />
            <Route path="/products/beaded-patches" element={<PublicLayout><BeadedPatchesPage /></PublicLayout>} />
            <Route path="/products/fringes"        element={<PublicLayout><FringesPage /></PublicLayout>} />
            <Route path="/products/:id"            element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
            <Route path="/search"             element={<PublicLayout><SearchPage /></PublicLayout>} />
            <Route path="/categories"          element={<PublicLayout><CategoriesPage /></PublicLayout>} />
            <Route path="/categories/:categorySlug" element={<PublicLayout><CategoryDetailPage /></PublicLayout>} />
            <Route path="/testimonials" element={<PublicLayout><TestimonialsPage /></PublicLayout>} />

            {/* ── Auth routes (no NavBar/Footer for clean auth UI) ── */}
            <Route path="/login"            element={<LoginPage />} />
            <Route path="/register"         element={<PublicLayout><RegisterPage /></PublicLayout>} />
            <Route path="/seller/register"  element={<SellerRegisterPage />} />
            <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
            <Route path="/reset-password"   element={<ResetPasswordPage />} />
            <Route path="/verify-email"     element={<VerifyEmailPage />} />

            {/* ── Protected role modules ─────────────────────────── */}
            <Route path="/user/*" element={
                <ProtectedRoute allowedRoles={['USER']}>
                    <Suspense fallback={<Loading />}><UserRoutes /></Suspense>
                </ProtectedRoute>
            } />

            <Route path="/seller/*" element={
                <ProtectedRoute allowedRoles={['SELLER']}>
                    <Suspense fallback={<Loading />}><SellerRoutes /></Suspense>
                </ProtectedRoute>
            } />

            <Route path="/admin/*" element={
                <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                    <Suspense fallback={<Loading />}><AdminRoutes /></Suspense>
                </ProtectedRoute>
            } />

            <Route path="/agent/*" element={
                <ProtectedRoute allowedRoles={['AGENT']}>
                    <Suspense fallback={<Loading />}><AgentRoutes /></Suspense>
                </ProtectedRoute>
            } />

            <Route path="/super-admin/*" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                    <Suspense fallback={<Loading />}><SuperAdminRoutes /></Suspense>
                </ProtectedRoute>
            } />

            {/* ── Request a Quote (mixed public + staff routes) ── */}
            <Route path="/request-a-quote/*" element={
                <PublicLayout>
                    <Suspense fallback={<Loading />}><QuoteRoutes /></Suspense>
                </PublicLayout>
            } />

            {/* ── Cart & Wishlist (public — login only required at checkout) ── */}
            <Route path="/cart"     element={<PublicLayout><CartPage /></PublicLayout>} />
            <Route path="/wishlist" element={<PublicLayout><WishlistPage /></PublicLayout>} />

            {/* ── Checkout & Order confirmation (login required) ─── */}
            <Route path="/checkout" element={
                <ProtectedRoute allowedRoles={['USER']}>
                    <PublicLayout><CheckoutPage /></PublicLayout>
                </ProtectedRoute>
            } />
            <Route path="/order-confirmation/:id" element={
                <ProtectedRoute allowedRoles={['USER']}>
                    <PublicLayout><OrderConfirmationPage /></PublicLayout>
                </ProtectedRoute>
            } />

            {/* ── Fallbacks ──────────────────────────────────────── */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
        </Routes>
    </BrowserRouter>
);

export default App;
